import { useEffect } from 'react';

import { fabric } from 'fabric';

import { ANNOTATION_COLORS, ANNOTATION_STROKES } from '~/contexts/CanvasContext';

import { IUseFabric } from './types';

export const useRectangleAnnotation = (props: IUseFabric & { zoomed?: boolean; }) => {
  const { ref, context, zoomed } = props;
  const { annotationEnabled, mode, color, stroke, zoom } = context;
  const strokeWidth = ANNOTATION_STROKES[stroke] / (zoomed ? zoom : 1);

  useEffect(() => {
    const canvas = ref.current;
    let isSelected = false;
    let rect: fabric.Rect | null = null;
    let origX = 0
    let origY = 0;
    const handleMouseDown = (event: fabric.IEvent<MouseEvent>) => {
      if (!isSelected && !canvas.getActiveObject()) {
        const pointer = canvas.getPointer(event.e);
        origX = pointer.x;
        origY = pointer.y;
        rect = new fabric.Rect({
          left: origX,
          top: origY,
          originX: 'left',
          originY: 'top',
          width: 0,
          height: 0,
          angle: 0,
          stroke: ANNOTATION_COLORS[color],
          strokeWidth,
          fill: '',
        });
        canvas.add(rect);
      }
    }
    const handleMouseMove = (event: fabric.IEvent<MouseEvent>) => {
      if (!isSelected && !canvas.getActiveObject() && rect) {
        const pointer = canvas.getPointer(event.e);

        if (origX > pointer.x) {
          rect.set({ left: Math.abs(pointer.x) });
        }
        if (origY > pointer.y) {
          rect.set({ top: Math.abs(pointer.y) });
        }

        rect.set({ width: Math.abs(origX - pointer.x) });
        rect.set({ height: Math.abs(origY - pointer.y) });

        canvas.renderAll();
      }
    }
    const handleMouseUp = () => {
      if (rect) {
        if (!rect.width && !rect.height) {
          canvas.remove(rect);
        } else {
          canvas.setActiveObject(rect);
        }
      }
      rect = null;
    }

    const handleSelection = () => {
      isSelected = true;
    }
    const handleDeselection = () => {
      // time out is required to avoid adding another rectangle when clicking outside of current drawing
      setTimeout(() => {
        isSelected = false;
      }, 100);
    }

    if (annotationEnabled && mode === 'rect') {
      canvas.on('mouse:down', handleMouseDown);
      canvas.on('mouse:move', handleMouseMove);
      canvas.on('mouse:up', handleMouseUp);
      canvas.on('selection:created', handleSelection);
      canvas.on('selection:updated', handleSelection);
      canvas.on('selection:cleared', handleDeselection);
      return () => {
        canvas.off('mouse:down', handleMouseDown);
        canvas.off('mouse:move', handleMouseMove);
        canvas.off('mouse:up', handleMouseUp);
        canvas.off('selection:created', handleSelection);
        canvas.off('selection:updated', handleSelection);
        canvas.off('selection:cleared', handleDeselection);
      }
    }
  }, [annotationEnabled, color, mode, ref, strokeWidth]);
}
